import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset'
import Switch from 'components/_UI/Switch';
import Text from 'components/_UI/Text'
import RichTextEditor from 'components/commons/RichText';
import { useEvent } from 'context/events/Event';
import fieldValidations from 'helpers/fieldValidations';
import masks from 'helpers/masks';
import React, { useEffect, useState, useCallback } from 'react'
import { Plus, Trash2, Calendar, Clock, FileText, Moon } from 'lucide-react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column'
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';

export default function BasicInfos({
    register,
    unregister,
    errors,
    reset,
    setValue,
    getValues,
    edit,
    descriptionError,
    onChangeDescriptionWithHtml,
    onChangeDescription,
    uploadImageDescription,
    pathImageDescription,
    errorTypeImageDescription,
    setErrorTypeImageDescription
}) {
    const { event, setEvent } = useEvent()

    const [multipleDays, setMultipleDays] = useState(false)

    const [field, setField] = useState({
        date: '',
        start: '',
        end: ''
    })
    const [fields, setFields] = useState([field])
    const [overnightFields, setOvernightFields] = useState({})

    const checkOvernight = useCallback((fieldIndex) => {
        const startValue = getValues(`start${fieldIndex}`)
        const endValue = getValues(`end${fieldIndex}`)

        if (!startValue || !endValue) {
            setOvernightFields(prev => ({ ...prev, [fieldIndex]: false }))
            return
        }

        const [startHour, startMinute] = startValue.split(':').map(Number)
        const [endHour, endMinute] = endValue.split(':').map(Number)

        if (isNaN(startHour) || isNaN(endHour)) return

        const startTotal = startHour * 60 + startMinute
        const endTotal = endHour * 60 + endMinute

        setOvernightFields(prev => ({ ...prev, [fieldIndex]: endTotal < startTotal }))
    }, [getValues])

    function validateEndTime(endValue, fieldIndex) {
        const startValue = getValues(`start${fieldIndex}`)

        if (!startValue || !endValue) {
            return true
        }

        const [startHour, startMinute] = startValue.split(':').map(Number)
        const [endHour, endMinute] = endValue.split(':').map(Number)

        if (isNaN(startHour) || isNaN(endHour)) return true

        const startTotal = startHour * 60 + startMinute
        const endTotal = endHour * 60 + endMinute

        if (endTotal === startTotal) {
            return 'O horário de término não pode ser igual ao horário de início'
        }

        checkOvernight(fieldIndex)

        return true
    }

    useEffect(() => {
        if (edit) {
            setFields(edit?.dates)
            setMultipleDays((edit?.dates.length > 1))
        }
    }, [edit, descriptionError, pathImageDescription])

    function renderNewEventDate() {
        return fields.map((el, index) => {
            if (index !== 0) {
                return (
                    <DayCard key={index}>
                        <DayHeader>
                            <DayBadge>{index + 1}</DayBadge>
                            <Text color={colors.nightDark} type='bold' name='small'>
                                Dia {index + 1}
                            </Text>
                        </DayHeader>

                        <DateTimeGrid>
                            <DateTimeField>
                                <Fieldset
                                    layout='squired'
                                    label='Data'
                                    mask={masks.date}
                                    name={`date${index + 1}`}
                                    register={register}
                                    validations={fieldValidations.date()}
                                />
                                {errors?.[`date${index + 1}`] && (
                                    <ErrorText>{errors[`date${index + 1}`].message}</ErrorText>
                                )}
                            </DateTimeField>

                            <DateTimeField onBlur={() => checkOvernight(index + 1)}>
                                <Fieldset
                                    layout='squired'
                                    label='Início'
                                    mask={masks.time}
                                    name={`start${index + 1}`}
                                    register={register}
                                    validations={fieldValidations.time('Horário de início')}
                                />
                                {errors?.[`start${index + 1}`] && (
                                    <ErrorText>{errors[`start${index + 1}`].message}</ErrorText>
                                )}
                            </DateTimeField>

                            <DateTimeField onBlur={() => checkOvernight(index + 1)}>
                                <Fieldset
                                    layout='squired'
                                    label='Término'
                                    mask={masks.time}
                                    name={`end${index + 1}`}
                                    register={register}
                                    validations={{
                                        ...fieldValidations.time('Horário de término'),
                                        validate: (value) => validateEndTime(value, index + 1)
                                    }}
                                />
                                {errors?.[`end${index + 1}`] && (
                                    <ErrorText>{errors[`end${index + 1}`].message}</ErrorText>
                                )}
                                {overnightFields[index + 1] && !errors?.[`end${index + 1}`] && (
                                    <OvernightHint>
                                        <Moon size={12} />
                                        Termina no dia seguinte
                                    </OvernightHint>
                                )}
                            </DateTimeField>
                        </DateTimeGrid>
                    </DayCard>
                )
            }
        })
    }

    function addNewField() {
        const newField = {
            date: '',
            start: '',
            end: ''
        };

        const updatedFields = [...fields, newField];
        setFields(updatedFields);

        const newIndex = updatedFields.length;
        setValue(`date${newIndex}`, '');
        setValue(`start${newIndex}`, '');
        setValue(`end${newIndex}`, '');
    }

    function removeField() {
        if (fields.length > 1) {
            const newData = fields.slice(0, -1);
            setFields(newData);

            const lastIndex = fields.length;
            const keysToRemove = [`date${lastIndex}`, `start${lastIndex}`, `end${lastIndex}`];

            keysToRemove.forEach(key => {
                setValue(key, undefined);
                unregister(key);
            });

            if (newData.length === 1) {
                setMultipleDays(false);
            }
        }
    }

    function onChangeDaysLenght() {
        if (!multipleDays) {
            setMultipleDays(!multipleDays)
            addNewField()
            return
        }

        setMultipleDays(!multipleDays)
        setFields([field])
        reset({ ...event, dates: [field] })
    }

    return (
        <Column>
            <SectionHeader>
                <SectionIcon><Calendar size={18} color={colors.occasionalPurple} /></SectionIcon>
                <Text type='bold' color={colors.nightDark}>
                    Informações básicas
                </Text>
            </SectionHeader>

            <Fieldset
                layout='squired'
                label='Nome do evento'
                name="name"
                register={register}
                validations={fieldValidations.generic('name', 2, 100)}
            />
            {errors?.name && (
                <ErrorText>{errors.name.message}</ErrorText>
            )}

            <DayCard style={{ marginTop: 16 }}>
                {multipleDays && (
                    <DayHeader>
                        <DayBadge>1</DayBadge>
                        <Text color={colors.nightDark} type='bold' name='small'>
                            Dia 1
                        </Text>
                    </DayHeader>
                )}

                <DateTimeGrid>
                    <DateTimeField>
                        <Fieldset
                            layout='squired'
                            label='Data'
                            mask={masks.date}
                            name="date1"
                            register={register}
                            validations={fieldValidations.date()}
                        />
                        {errors?.date1 && (
                            <ErrorText>{errors.date1.message}</ErrorText>
                        )}
                    </DateTimeField>

                    <DateTimeField onBlur={() => checkOvernight(1)}>
                        <Fieldset
                            layout='squired'
                            label='Início'
                            mask={masks.time}
                            name="start1"
                            register={register}
                            validations={fieldValidations.time()}
                        />
                        {errors?.start1 && (
                            <ErrorText>{errors.start1.message}</ErrorText>
                        )}
                    </DateTimeField>

                    <DateTimeField onBlur={() => checkOvernight(1)}>
                        <Fieldset
                            layout='squired'
                            label='Término'
                            mask={masks.time}
                            name={'end1'}
                            register={register}
                            validations={{
                                ...fieldValidations.time('Horário de término'),
                                validate: (value) => validateEndTime(value, 1)
                            }}
                        />
                        {errors?.end1 && (
                            <ErrorText>{errors.end1.message}</ErrorText>
                        )}
                        {overnightFields[1] && !errors?.end1 && (
                            <OvernightHint>
                                <Moon size={12} />
                                Termina no dia seguinte
                            </OvernightHint>
                        )}
                    </DateTimeField>
                </DateTimeGrid>
            </DayCard>

            <Switch
                checked={multipleDays}
                onChange={onChangeDaysLenght}
                label='O evento tem mais de um dia de duração'
                marginTop='8px'
            />

            {multipleDays && (
                <MultipleDaysContainer>
                    {renderNewEventDate()}

                    <DayActionsRow>
                        <AddDayButton type="button" onClick={() => addNewField()}>
                            <Plus size={16} />
                            Adicionar dia
                        </AddDayButton>

                        {fields.length > 2 && (
                            <RemoveDayButton type="button" onClick={() => removeField()}>
                                <Trash2 size={16} />
                                Remover último
                            </RemoveDayButton>
                        )}
                    </DayActionsRow>
                </MultipleDaysContainer>
            )}

            <DescriptionSection>
                <SectionHeader>
                    <SectionIcon><FileText size={18} color={colors.occasionalPurple} /></SectionIcon>
                    <Text type='bold' color={colors.nightDark}>
                        Descrição do evento
                    </Text>
                </SectionHeader>
                <Text color={colors.night} name='small' marginBottom='12px'>
                    Descreva os detalhes, atrações e informações importantes do evento.
                </Text>

                <RichTextEditor
                    edit={edit}
                    onChangePureText={e => onChangeDescription(e)}
                    onChange={e => onChangeDescriptionWithHtml(e)}
                    onToggleImage={e => uploadImageDescription(e)}
                    pathImageDescription={pathImageDescription}
                    errorTypeImageDescription={errorTypeImageDescription}
                    setErrorTypeImageDescription={setErrorTypeImageDescription}
                />
                {errorTypeImageDescription && (
                    <ErrorText>{errorTypeImageDescription}</ErrorText>
                )}
                {descriptionError && (
                    <ErrorText>O campo descrição é obrigatório</ErrorText>
                )}
            </DescriptionSection>
        </Column>
    )
}

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const DayCard = styled.div`
    background: ${colors.neutralBackground};
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 16px;
`;

const DayHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

const DayBadge = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
`;

const DateTimeGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 4px;

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: 1.2fr 1fr 1fr;
        gap: 12px;
    }
`;

const DateTimeField = styled.div`
    width: 100%;
`;

const ErrorText = styled.p`
    color: ${colors.danger};
    font-size: 12px;
    margin-top: 4px;
    padding-left: 2px;
`;

const MultipleDaysContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
`;

const DayActionsRow = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

const DayActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;
    border: none;
`;

const AddDayButton = styled(DayActionButton)`
    background: #F3EAFF;
    color: ${colors.occasionalPurple};

    &:hover {
        background: #E8D5FF;
    }
`;

const RemoveDayButton = styled(DayActionButton)`
    background: #FEF2F2;
    color: ${colors.danger};

    &:hover {
        background: #FEE2E2;
    }
`;

const OvernightHint = styled.p`
    display: flex;
    align-items: center;
    gap: 4px;
    color: ${colors.occasionalPurple};
    font-size: 12px;
    font-weight: 600;
    margin-top: 4px;
    padding-left: 2px;
`;

const DescriptionSection = styled.div`
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid ${colors.neutralDark};
`;
